import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, CircularProgress } from '@mui/material';
import { walletAPI } from '../../services/api';
import { formatCurrency, formatRenewalDate, maskPolicyNumber } from '../../utils/renewalHelpers';
import { MAX_POLICIES_PER_COMPARISON } from '../../config/regional';

export default function MultiPolicyComparison() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawIds = searchParams.get('ids')?.split(',').filter(Boolean) || [];
  const ids = rawIds.slice(0, MAX_POLICIES_PER_COMPARISON);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(!!ids.length);

  useEffect(() => {
    if (ids.length === 0) {
      setPolicies([]);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const list = await Promise.all(ids.map((id) => walletAPI.getPolicy(id).catch(() => null)));
        setPolicies(list.map((d) => d?.data ?? d).filter(Boolean));
      } catch (e) {
        setPolicies([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [ids.join(',')]);

  if (loading) return <Container sx={{ py: 3 }}><CircularProgress /></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Compare policies</Typography>
      {ids.length === 0 ? (
        <Typography color="text.secondary">{`Add policy IDs to the URL: /renewals/compare?ids=id1,id2,id3 (max ${MAX_POLICIES_PER_COMPARISON} policies)`}</Typography>
      ) : policies.length === 0 ? (
        <Typography color="text.secondary">Could not load one or more policies.</Typography>
      ) : (
        <Box sx={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #ddd' }}>Policy</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #ddd' }}>Insurer</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #ddd' }}>End date</th>
                <th style={{ textAlign: 'right', padding: 8, borderBottom: '1px solid #ddd' }}>Premium</th>
                <th style={{ padding: 8, borderBottom: '1px solid #ddd' }}></th>
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <tr key={p.id}>
                  <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{p.policy_name || maskPolicyNumber(p.policy_number)}</td>
                  <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{p.insurer_name}</td>
                  <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>{formatRenewalDate(p.end_date)}</td>
                  <td style={{ padding: 8, borderBottom: '1px solid #eee', textAlign: 'right' }}>{formatCurrency(p.premium_amount)}</td>
                  <td style={{ padding: 8, borderBottom: '1px solid #eee' }}>
                    <Button size="small" onClick={() => navigate(`/renewals/${p.id}`)}>Details</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
      <Button sx={{ mt: 2 }} onClick={() => navigate('/renewals')}>Back to Renewals</Button>
    </Container>
  );
}
